import type { Product } from '@/lib/types';

/*
 * The wording and the derivations `/b2b-service-maps` runs on — the ONE copy of
 * each.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES.** The page owns its requests; this module
 * answers *"given what the server said, what may the screen say, and what may it
 * offer?"*. Same standing as `event-map-rules.ts`, `credit-notes.ts` and
 * `company-projects.ts`.
 *
 * ── 🔴 THE SENTENCES BELOW ARE NOT THE `/event-maps` ONES, AND THE DIFFERENCE
 *    IS THE POINT ─────────────────────────────────────────────────────────────
 *
 * `/event-maps` is the same SHAPE and its two consequences are FALSE here, both
 * of them, in the reassuring direction. Measured in `emerge-backend-app`'s
 * `b2b-proposal.repository.ts` (`findServiceKeys`, the `is_active` section) and
 * `migrations/231_create_f_b2b_proposal_service_map.sql`:
 *
 *  - **An unmapped key REFUSES NOTHING.** On the interest form a key resolving
 *    to nothing is a 422 at submission time; here the owner ruled the opposite
 *    (contract §4.3) — *"a proposal is never refused because a mapping row is
 *    missing"*. The join is LEFT and the key comes back with `name: null`. So
 *    every sentence on this screen that would borrow `/event-maps`' urgency
 *    would be describing a refusal that cannot happen.
 *  - **`is_active` IS READ BY NOTHING.** No path filters on it, deliberately:
 *    resolution runs at READ time for stored submissions too, so a filter would
 *    not hide a key, it would silently UNNAME history. So "while this is off the
 *    form refuses submissions naming this key" — the `/event-maps` wording — is
 *    a flat falsehood here.
 *
 * ⚠️ **Re-read those two artefacts rather than this paragraph.** Both are
 * recorded as UNTAKEN decisions at their own sites: the second decision that
 * would give `is_active` meaning (whether a NEW submission naming a retired key
 * should report it unresolved) has not been made, and taking it would put a
 * filter on the create path only. **Do not infer it from the column existing**,
 * and do not word a control as though it had been taken.
 */

/* -------------------------------------------------------------------------- */
/* The two consequences                                                       */
/* -------------------------------------------------------------------------- */

/**
 * What ticking this off actually does — which today is **nothing observable**.
 *
 * ⛔ **NOT HELPER TEXT, AND A SWEEP MUST NOT TAKE IT.** The no-helper-text rule
 * keeps four kinds of sentence, and this is the one that states **a fact the
 * reader cannot otherwise see**: a tickbox whose effect is nil is
 * indistinguishable, from the control alone, from a tickbox that closes a door.
 * A reader who unticks it and is told nothing will believe they have retired a
 * service. They have not.
 *
 * ⛔ **IT MUST NEVER CLAIM THE KEY STOPS RESOLVING.** That is the sentence
 * `/event-maps` carries, it is the obvious thing to write, and it is false:
 * nothing anywhere reads this column. Claiming it would have somebody untick a
 * live mapping believing they had closed it, while every proposal went on
 * resolving exactly as before — a wrong belief about a public form, which is the
 * one thing this app cannot see happening.
 *
 * ⚠️ **It says the decision is open rather than implying the wiring is coming.**
 * Nobody has ruled on what a retired mapping should change, so "not yet" would
 * be a promise nobody has made.
 */
export const RETIRE_CONSEQUENCE =
  'Nothing reads this today: a key resolves to its product whether this is on or off, on new proposals and on ones already stored. It records that the mapping is no longer offered — what that should change has not been decided.';

/**
 * What Delete does, and — the reason this sentence exists at all — **what it
 * does NOT do**.
 *
 * 🔴 **A READER WILL ASSUME THE OPPOSITE, BECAUSE EVERY OTHER MASTER HERE
 * CASCADES OR RESTRICTS.** Deleting a city, a project or a currency is a
 * question about the records pointing at it. This is not: a submission's service
 * keys live in `f_b2b_proposal_service_keys` and are joined to LEFT, so **no
 * enquiry is touched, nothing an institution asked for is erased, and nothing is
 * refused afterwards.** Without that said out loud, a dialog about a mapping
 * reads as a dialog about the proposals behind it.
 *
 * ⚠️ **AND IT NAMES THE HALF THAT IS REAL, WHICH IS THE NAMES.** Resolution runs
 * at READ time on every submission, not at the moment one arrives, so removing
 * the row does reach history: rows that have been reading as a product name go
 * back to reading as the raw key. That is a real loss of legibility and it must
 * not be hidden behind the reassurance above it.
 *
 * ⛔ **NO UNDO IS PROMISED, AND `/event-maps`' ALTERNATIVE IS NOT OFFERED.** That
 * dialog points at its tickbox as the reversible way to do the same job. Here
 * the tickbox does **nothing at all**, so naming it would be offering a control
 * that cannot achieve what the sentence claims for it. What IS true — and is a
 * fact about the same read-time join rather than a promise of a control — is
 * that entering the key again makes those names resolve once more.
 */
export const DELETE_CONSEQUENCE =
  'No enquiry is lost: every proposal keeps the service key exactly as it was sent, and none is refused for want of a mapping. What goes is the name — proposals naming this key read as the raw key again, here and everywhere else, until the key is mapped afresh.';

/* -------------------------------------------------------------------------- */
/* Telling one product from another                                           */
/* -------------------------------------------------------------------------- */

/**
 * The part of a product that is NOT its name.
 *
 * 🔴 **IT EXISTS BECAUSE THE NAMES SHARE LONG PREFIXES AND DIFFER AT THE TAIL,
 * WHICH IS EXACTLY WHAT A TRUNCATING TRIGGER EATS.** `Sales - Business School
 * Festivals: Participation Fee` and `Sales - Business School Festivals: Add-ons`
 * are two products a person picks between on this screen, and a `Combobox`
 * renders `label` alone and truncates it. This codebase already records that the
 * only thing marking an add-on for years was the words `: Add-ons` in an
 * admin-editable `productName` — so the tail is where the meaning is, and the
 * tail is what goes missing.
 *
 * ⚠️ **`isAddOn` IS READ, NOT THE NAME.** A migration seeded that flag once, by
 * name, and this codebase's standing rule is that **nothing may match the name
 * again** — an ordinary rename is what switched a feature off three times here.
 *
 * ⚠️ **Returns `''` when a product carries neither**, and the caller decides what
 * to do with that. ⛔ It must NOT be padded into a plausible-looking string: a
 * product with nothing to tell it apart is a fact about the catalogue a person
 * needs before mapping a service to it.
 *
 * ⛔ **`unitPrice` is deliberately NOT here.** A mapping is about which product a
 * service IS, not what it costs, and a price beside a name on a configuration
 * screen reads as a quote. It is also nullable in a way that means *unpriced*
 * rather than free, which is a distinction this cell has no room to make.
 */
export function productSubtitle(product: Product): string {
  const parts: string[] = [];

  /*
   * `productCode` is nullable and blank-able. A code is the shortest thing that
   * separates two long names, so it leads when it is there.
   */
  const code = product.productCode === null ? '' : product.productCode.trim();
  if (code) parts.push(code);

  if (product.isAddOn) parts.push('add-on');

  return parts.join(' · ');
}

/* -------------------------------------------------------------------------- */
/* Reading a service key                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The service keys a B2B proposal can name, as far as anybody here can
 * establish — held as SUGGESTIONS, so an administrator picks a slug instead of
 * typing one out of another repository from memory.
 *
 * ⛔ **WHAT THAT FORM DISPLAYS AND WHAT THEIR API ACCEPTS ARE DIFFERENT SETS,
 * AND CONFLATING THEM IS THE ONE MISREADING THAT MATTERS.** Their backend
 * validates a submitted service as
 * `['string', 'distinct', 'exists:d_products,product_slug']` — so **any live
 * product slug in their catalogue is accepted**, and the form's own list is a
 * hand-curated subset somebody chose to surface. A key outside it is not a copy
 * that has failed to catch up: it can be a perfectly ordinary submission that
 * was never going to be on that form at all.
 *
 * 🔴 **THERE ARE THREE SETS AND ALL THREE DIFFER — DISPLAY 11, ACCEPT 14,
 * MARKETING PAGES 11.** ⚠️ *SUPERSEDED 2026-09-20: "DISPLAY 10, ACCEPT 13".*
 * Seeding this from the display set alone, which is the obvious thing to do
 * because it is the only one written down as a literal, would **still silently
 * omit three keys that are perfectly valid submissions**:
 * `stem-masters-festival` (split from the Business School Festival on
 * 2026-08-08), `meetups` and `student-advising` — **unchanged by today's
 * addition**, because `edumate` joined both sets at once rather than one of
 * them. That is the copy-of-a-copy failure with numbers on it, and the numbers
 * are recorded here because they are what makes the argument concrete — *"these
 * might drift"* is a caution, *"they are 11, 14 and 11, and two of the three
 * moved in a single afternoon"* is a measurement.
 *
 * ⚠️ **THE MARKETING-PAGE FIGURE WAS NOT RE-MEASURED TODAY** and is carried
 * forward from the earlier reading, so the three numbers above are of two
 * different ages. **Do not read the trio as one snapshot.**
 *
 * 🔴 **THE THREE SETS MOVE FOR THREE DIFFERENT REASONS, SO NO ONE OF THEM CAN
 * REPORT ON THE OTHERS.** A product added to their catalogue widens the accept
 * set and nothing else; an edit to their `allServices` literal moves the
 * display set and nothing else; a marketing page shipped moves the third.
 * **Neither repository can see from its own side how far this copy has fallen
 * behind any of them**, which is a stronger reason for the free-text path below
 * than mere staleness, and it is the reason this constant must never become a
 * validation.
 *
 * ── 🔴 AND THAT PREDICTION FIRED ON DAY ONE — 2026-09-20 ─────────────────────
 *
 * **THIS LIST WENT STALE WITHIN HOURS OF BEING COMMITTED, IN BOTH HALVES AT
 * ONCE.** The thirteen were written down as ten measured plus three reported;
 * the same afternoon `edumate` arrived and moved **the accept set AND the
 * display set together**, so the copy had decayed in both directions before the
 * commit that introduced it was a day old.
 *
 * ⚠️ **Record that as the argument landing, not as a routine update.** The case
 * for the endpoint that retires this constant rested on *"a copy of a copy goes
 * stale silently"* — which was a prediction when it was written and is now a
 * measurement with a date on it. **A later reader deciding whether this copy is
 * tolerable needs to know it rotted on day one rather than in six months**, and
 * that it did so without anything in this repository failing, erroring or
 * looking different: the picker simply went on offering one fewer key than the
 * form did.
 *
 * ⛔ **AND THE UPDATE BELOW DOES NOT MEAN THE LIST IS NOW CORRECT.** It is
 * correct *as of a reading taken on 2026-09-20* — which is precisely the claim
 * this header warns is worth nothing tomorrow. Nothing here can see their
 * catalogue, so the only honest statement about the fourteen is that fourteen
 * is what we had been told by that afternoon.
 *
 * ⚠️ **PROVENANCE IS NOT UNIFORM ACROSS THIS LIST AND MUST NOT BE READ AS IF IT
 * WERE. THERE ARE THREE GROUPS NOW, NOT TWO**, and the three blocks below are
 * kept apart for exactly that reason:
 *
 *  1. **The first TEN are MEASURED HERE**, from `b2b-app`'s
 *     `src/components/home/ProposalModal.tsx` — the `slug` values of
 *     `allServices` at lines 26-42, in that file's own order. Anybody in this
 *     repository can re-open that file and diff it.
 *  2. **The next THREE are REPORTED**, by the team that owns that data, from a
 *     production query on 2026-09-20. Their API accepts them; their form does
 *     not offer them.
 *  3. 🔴 **`edumate` is REPORTED TOO — and is the first key that is BOTH
 *     accepted AND offered with neither half measured from here.** Reported the
 *     same day, hours after the three.
 *
 * ⛔ **WE CANNOT READ THEIR DATABASE AND THERE IS NO ENDPOINT THAT WOULD LET US**
 * — which is why groups 2 and 3 are claims with a date on them where group 1 is
 * a file. **Re-ask rather than assuming the four reported keys are still
 * right**, and **never present the fourteen as one uniformly-measured list.**
 *
 * ⚠️ **THE ORDER THEY DID IT IN IS WORTH RECORDING, BECAUSE THE OBVIOUS ORDER IS
 * WORSE FOR THE VISITOR.** They created the product row (`p15`, slug `edumate`,
 * active) and **then** shipped the form entry, deliberately. Shipping the form
 * entry first would have turned *"Edumate is not offered"* into *"Edumate is
 * offered, and then refused once the visitor has filled the form in"* — which
 * loses the enquiry **and** their time, where the original bug lost only the
 * enquiry. **Row first, then the offer**, on anything crossing that wire.
 *
 * ⛔ **IT IS A SUGGESTION LIST AND NEVER A VALIDATION. NOTHING MAY BRANCH ON
 * MEMBERSHIP.** An unlisted key must not narrow a type, gate a Save, mark a
 * field invalid, or colour a row as unrecognised. Refusing one would refuse a
 * key their own API accepts, from a list neither their form nor their database
 * has ever agreed to be bound by — a frontend convention dressed as a server
 * rule. When this list is short, **no build breaks and no request fails**: the
 * picker does not offer the key, the administrator types it, and the mapping is
 * correct.
 *
 * ⚠️ **AND THE PICKER CANNOT BE EXHAUSTIVE EVEN IN PRINCIPLE, WHICH IS WHY FREE
 * TEXT IS THE MAIN ROAD AND NOT THE FALLBACK.** A visitor can ask for a service
 * that has **no product row on their side at all**, and that submission is
 * refused by their validator whatever this app offers. So the ceiling on this
 * list is not *"how current is our copy"* but *"what exists in a table we do not
 * own"*, and no amount of maintenance closes the gap. **Treat the picker as a
 * convenience over the typing, never as the set of legal answers.**
 *
 * 🔴 **EDUMATE IS THE WORKED EXAMPLE, AND UNTIL TODAY IT WAS THE LIVE ONE.** An
 * Edumate page was up and asking for a service **their catalogue did not
 * carry**, so a visitor could ask for it from that page and be refused by the
 * validator *whatever this app offered* — the gap was in their `d_products`,
 * not in any copy, and nothing on this list could have closed it from here.
 * ✅ **They closed it on 2026-09-20 by creating the row**, which is the only
 * side it could be closed from. ⛔ **So do not read the entry below as this list
 * catching up**: it is the far side of the wire moving, and it is an instance of
 * this paragraph rather than a counter-example to it — the next page they put up
 * naming something uncatalogued reopens the same gap, and this constant will
 * again have nothing to say about it.
 *
 * ⚠️ **The two failures this module has now seen are mirror images, and both
 * lose the enquiry.** `ai-visa-tutor` was **acceptable and not offered** for six
 * weeks (below); Edumate was **askable and not acceptable**. Neither is visible
 * from this repository and neither is fixed by maintaining this list.
 *
 * ⚠️ **THE DISPLAY HALF HAS ALREADY BEEN WRONG ONCE, WHILE THE ACCEPT HALF WAS
 * FINE THROUGHOUT.** `ai-visa-tutor` carried `slug: null` in that file until
 * 2026-09-19, so it was filtered out of the payload entirely: interest in it
 * reached no destination at all, and a visitor who wanted only that service
 * submitted an empty `services` array that read as *"asked for nothing"*. Their
 * own comment at lines 36-40 records it — and records that the `d_products`
 * row had existed, live, since 2026-08-09 the whole time. **The key was
 * acceptable for six weeks before it was offerable**, which is the two sets
 * coming apart, in the direction that loses an enquiry.
 *
 * ⚠️ **SEVERAL SLUGS ARE NOT DERIVABLE FROM THE LABEL OR THE ROUTE BESIDE THEM,
 * WHICH IS WHERE THE PICKER EARNS ITS PLACE.** *"Student Recruitment Events"*,
 * on the route `student-recruitment-fairs`, sends
 * `seed-business-school-festival` — a slug sharing no word with either.
 * *"Virtual Masterclasses"* sends bare `masterclasses`. *"Scholarships
 * Program"* — plural — sends `scholarship-program`, singular, while *"School
 * Profile"* — singular — sends `school-profiles`, plural, so the same
 * transformation is not even applied consistently. **Typing one of these from
 * memory gets it wrong in a way that produces a mapping row which looks
 * configured and resolves nothing**, and nothing on this screen would report
 * it. ⛔ **No count is written here** — the sender edits that file, and a
 * figure would be a second thing to keep in step with a list this module
 * already copies once.
 *
 * ⚠️ **THE FIRST BLOCK IS THE SENDER'S SOURCE ORDER, DELIBERATELY, NOT
 * ALPHABETICAL** — so somebody holding it beside theirs is comparing two lists
 * in one order and can see at a glance which entry is missing. Sorting it would
 * make every future diff against the source a manual reconciliation.
 *
 * ⛔ **THAT IS A CLAIM ABOUT THE FIRST BLOCK ALONE, AND IT IS WHY THE REPORTED
 * KEYS ARE APPENDED RATHER THAN SLOTTED IN.** Nobody here knows where `edumate`
 * or the other three sit in their file, so placing them by guess would corrupt
 * the one property the ordering exists to give — a reader diffing against the
 * source would find an entry out of position and have no way to tell a guess
 * from a genuine divergence. **Append; never interleave.**
 *
 * ⚠️ **THIS APP HAS NO OTHER SOURCE FOR *WHICH KEYS EXIST*, WHICH IS WHY A COPY
 * WAS CHOSEN OVER A FETCH.** `GET /b2b-service-maps` returns only the keys
 * ALREADY MAPPED and takes no query parameters at all — its schema is an empty
 * `.strict()` object (`b2b-service-map.schema.ts:257`) — so it can never answer
 * *"what has the form got that we have not mapped?"*, which is the one question
 * this list is here for.
 *
 * ✅ **AND THE COPY HAS A KNOWN END: they have undertaken to publish the list
 * as an endpoint, seeded from their products table — reaffirmed when `edumate`
 * was reported.** ⚠️ **The observable event is that read EXISTING, not the
 * promise and not the reaffirmation** — when it does, this constant is
 * **deleted** rather than maintained, and the picker offers what their
 * catalogue actually accepts instead of what somebody last typed into a form.
 * ⛔ **No date is written here and none should be**: a commitment restated is
 * still a commitment, and the staleness recorded above happened while this
 * paragraph already said the endpoint was coming.
 */
export const KNOWN_SERVICE_KEYS: readonly string[] = [
  /*
   * The TEN MEASURED FROM `ProposalModal.tsx`, in that file's own source order
   * so this list can be read side by side with theirs.
   * ⚠️ NOT "the ten the form offers" — the form offers ELEVEN since 2026-09-20,
   * and the eleventh is `edumate`, two blocks below. These ten are the ones
   * somebody here has read in that file, which is a claim about our evidence
   * rather than about their form.
   */
  'seed-business-school-festival',
  'masterclasses',
  'campus-tours',
  'school-profiles',
  'challenge-hub',
  'in-country-representation',
  'lead-and-application-engagement',
  'scholarship-program',
  'yield-events',
  'ai-visa-tutor',

  /*
   * THREE MORE THEIR API ACCEPTS AND THEIR FORM DOES NOT OFFER — kept in a
   * block of their own rather than merged, so the block above stays diffable
   * against the source. Still three: today's addition joined both sets at once
   * and is therefore not one of these.
   * ⚠️ REPORTED, NOT MEASURED HERE: provenance group 2 in the doc above.
   */
  'stem-masters-festival',
  'meetups',
  'student-advising',

  /*
   * 🔴 ONE MORE, ADDED 2026-09-20, AND IT BELONGS TO NEITHER BLOCK ABOVE.
   * `edumate` is accepted AND offered, so it is not an accepted-but-unoffered
   * key; and it is REPORTED rather than read out of `ProposalModal.tsx`, so it
   * is not one of the measured ten. **A block of its own, for two reasons that
   * are both about the next reader diffing this against its sources:** merging
   * it into the first block would claim we had read it there (we have not) and
   * would need its position in their source order (we do not know it), and
   * appending leaves BOTH blocks above byte-identical, so each still diffs
   * cleanly against the source it actually came from.
   * ⚠️ PROVENANCE GROUP 3 — reported, unverified from here. See the doc above.
   */
  'edumate',
];

/**
 * Whether a key can be sent at all.
 *
 * ⛔ **THE ONLY CLIENT-SIDE CHECK ON A KEY, AND IT IS DELIBERATELY THIS WEAK.**
 * The vocabulary is the SENDER's — these are `d_products.product_slug` values in
 * another repository's database — and none of its spelling is this app's to
 * police. A pattern invented here would refuse a key that is correct at their
 * end, with nothing on screen able to explain why: a frontend convention dressed
 * as a server rule. **An empty box is the one thing that cannot be right**, and
 * everything else is the server's to refuse in its own words.
 *
 * ⛔ **AND NOTHING HERE FOLDS CASE.** The stored column and the submission's own
 * are both `utf8mb4_unicode_ci`, so the UNIQUE index already matches
 * case-insensitively and `AI_VISA` collides with `ai_visa` as a 409 the server
 * words itself. Lower-casing on the way in would silently rewrite a key the
 * sender spelled deliberately — this app stores what it was given.
 */
export function serviceKeyIsEnterable(raw: string): boolean {
  return raw.trim().length > 0;
}
